import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LayoutGrid, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "../components/site/PageTitle";
import { GangCanvas } from "../components/gang/GangCanvas";
import { GangControls } from "../components/gang/GangControls";
import { GangUploader } from "../components/gang/GangUploader";
import { useSite } from "../context/SiteContext";
import { useCart } from "../context/CartContext";
import { api, errMsg } from "../lib/api";
import { GAP, box, isOff, nextSpot, overlappingIds, pack, renderGang, usedLength } from "../lib/gang";

const uid = () => `g${Date.now()}${Math.random().toString(36).slice(2, 6)}`;

export default function GangSheet() {
  const { settings: s = {} } = useSite();
  const { add } = useCart();
  const nav = useNavigate();
  const W = Number(s.gang_width) || 22;
  const MIN = Number(s.gang_min_images) || 6;
  const sizes = useMemo(() => [...(s.gang_sizes || [])].sort((a, b) => a.length - b.length), [s.gang_sizes]);
  const [sizeIdx, setSizeIdx] = useState(0);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [f, setF] = useState({ quantity: 1, notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (sizeIdx >= sizes.length && sizes.length) setSizeIdx(0); }, [sizes, sizeIdx]);
  if (!sizes.length || s.gang_enabled === false) return <div className="py-32 text-center text-slate-400">Gang sheet builder is not available right now.</div>;

  const size = sizes[sizeIdx] || sizes[0];
  const L = size.length;
  const offCount = items.filter((it) => isOff(it, W, L)).length;
  const overlaps = overlappingIds(items);
  const sel = items.find((it) => it.id === selected);
  const patch = (id, p) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));

  const addItem = (base) => setItems((xs) => {
    const it = { ...base, id: uid(), rot: 0, w: Math.min(4, W - 2 * GAP) };
    return [...xs, { ...it, ...nextSpot(xs, it, W) }];
  });
  const duplicate = () => sel && setItems((xs) => [...xs, { ...sel, id: uid(), ...nextSpot(xs, sel, W) }]);
  const autoArrange = () => {
    const { placed, length } = pack(items, W);
    setItems(placed);
    if (length > L) {
      const fit = sizes.findIndex((z) => z.length >= length);
      if (fit >= 0) { setSizeIdx(fit); toast.success(`Arranged. Switched to ${W}" × ${sizes[fit].length}" so everything fits.`); }
      else toast.error("Your pictures don't fit on the largest sheet. Make some smaller or remove a few.");
    } else toast.success("Pictures arranged");
  };

  const addToCart = async () => {
    if (items.length < MIN) return toast.error(`Gang sheets need at least ${MIN} pictures`);
    if (offCount) return toast.error("Some pictures are off the sheet (red outline). Move them or pick a longer sheet.");
    if (overlaps.size) return toast.error("Some pictures overlap (orange outline). Move them apart or use Auto Arrange.");
    setBusy(true);
    try {
      const blob = await renderGang(items, W, L, 20);
      const fd = new FormData();
      fd.append("file", blob, "gang-sheet.png");
      const { data } = await api.post("/uploads/preview", fd);
      add({ item_type: `Gang Sheet ${W}" × ${L}"`, colors: "", placement: "", sizes: "", quantity: Math.max(1, parseInt(f.quantity, 10) || 1), notes: f.notes,
        gang: { length: L, width: W, price: size.price, image_count: items.length, preview_path: data.path, preview_url: data.url,
          layout: items.map((it) => { const { bw, bh } = box(it); return { art_path: it.path, x: +it.x.toFixed(3), y: +it.y.toFixed(3), w: +bw.toFixed(3), h: +bh.toFixed(3), rot: it.rot }; }) } });
      toast.success("Gang sheet added to cart");
      nav("/checkout");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="pb-20">
      <PageTitle badge="DTF Transfers" title="Gang Sheet Builder" subtitle={`Pick a sheet size, upload your pictures and arrange them on a ${W}" wide sheet. Minimum ${MIN} pictures per sheet.`} testId="gang-title" />
      <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-[280px_1fr_320px]">
        <aside className="panel h-fit space-y-2 p-5 lg:sticky lg:top-28">
          <h2 className="red-title mb-2 text-xl">1. Sheet size</h2>
          {sizes.map((z, i) => (
            <button key={z.length} onClick={() => setSizeIdx(i)} className={`flex w-full items-center justify-between rounded-lg border px-4 py-2.5 font-display text-lg font-bold transition-colors ${i === sizeIdx ? "border-[#E5202A] bg-[#E5202A]/15" : "border-white/10 hover:border-white/30"}`} data-testid={`gang-size-${z.length}`}>
              <span>{W}" × {z.length}"</span><span className="text-[#E5202A]">${z.price}</span>
            </button>
          ))}
        </aside>
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-display text-xl font-bold uppercase">{W}" × {L}" sheet</span>
            <span className="text-slate-400">Used: {usedLength(items).toFixed(1)}" of {L}"</span>
          </div>
          <div className="max-h-[75vh] overflow-y-auto rounded-xl border border-white/10 bg-[#0E1018] p-2" data-testid="gang-canvas-scroll">
            <GangCanvas items={items} W={W} L={L} selected={selected} overlaps={overlaps} onSelect={setSelected} onMove={patch} />
          </div>
        </section>
        <aside className="panel h-fit space-y-4 p-5 lg:sticky lg:top-28">
          <h2 className="red-title text-xl">2. Pictures</h2>
          <GangUploader onAdd={addItem} />
          <div className={`rounded-lg px-4 py-3 text-sm font-semibold ${items.length >= MIN ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="gang-count">
            {items.length} / {MIN} pictures minimum{items.length < MIN ? ` · add ${MIN - items.length} more` : " ✓"}
          </div>
          {items.length > 1 && <button onClick={autoArrange} className="btn-ghost w-full !py-2" data-testid="gang-auto-arrange-button"><LayoutGrid className="h-4 w-4" />Auto Arrange</button>}
          {sel && <GangControls item={sel} W={W} onChange={(p) => patch(sel.id, p)} onDuplicate={duplicate} onRemove={() => { setItems((xs) => xs.filter((x) => x.id !== sel.id)); setSelected(null); }} />}
          {offCount > 0 && <p className="flex items-center gap-2 text-sm text-red-400" data-testid="gang-off-sheet-warning"><AlertTriangle className="h-4 w-4" />{offCount} picture(s) off the sheet</p>}
          {overlaps.size > 0 && <p className="flex items-center gap-2 text-sm text-orange-400" data-testid="gang-overlap-warning"><AlertTriangle className="h-4 w-4" />{overlaps.size} picture(s) overlapping</p>}
          <h2 className="red-title text-xl">3. Details</h2>
          <div className="grid grid-cols-[90px_1fr] gap-3">
            <div><label className="label">Sheets</label><input className="field" type="number" min="1" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="gang-quantity-input" /></div>
            <div><label className="label">Price / sheet</label><p className="py-3 font-display text-2xl font-black text-[#E5202A]" data-testid="gang-price">${size.price}</p></div>
          </div>
          <div><label className="label">Notes</label><textarea className="field min-h-[60px]" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="gang-notes-input" /></div>
          <button onClick={addToCart} disabled={busy || items.length < MIN} className="btn-red w-full" data-testid="gang-add-to-cart-button">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />} {items.length < MIN ? `Need ${MIN - items.length} more picture(s)` : "Add to Cart"}
          </button>
          <p className="text-xs text-slate-500">We print your sheet and you pick up the transfers to press onto your own items. Sizes shown are print sizes; we'll check your files before sending an invoice.</p>
        </aside>
      </div>
    </div>
  );
}
